import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
  useRef,
  useState,
  useEffect,
  type ReactNode,
} from "react";
import * as api from "../api/disciplio-api";
import type { DisciplioApiError } from "../api/disciplio-api";
import { loadState } from "../disciplio/storage";
import {
  emptyGoalCreationDrafts,
  goalDraftOwnerEmail,
  loadGoalCreationDrafts,
  saveGoalCreationDrafts,
  type DailyGoalCreationDraft,
  type GlobalGoalCreationDraft,
  type GoalCreationDrafts,
  type MonthlyGoalCreationDraft,
  type WeeklyGoalCreationDraft,
  type YearGoalCreationDraft,
} from "../disciplio/goal-creation-drafts";
import type {
  ActionPrefill,
  AppState,
  DailyItem,
  HabitTimeScope,
  CalendarHabitTimeScopeMode,
  DisciplioSettings,
  MonthlyGoal,
  RagSource,
  UserProfile,
  WeeklyGoal,
} from "../disciplio/types";
import { reducer } from "./disciplio-context-state";
import {
  usePersistedDisciplioState,
  usePlanLoader,
  useRemoteStateSynchronizer,
} from "./disciplio-context-bootstrap";
import { useBrowserStorageConsent } from "./browser-storage-consent-context";
import { useDisciplioActions } from "./disciplio-context-actions";
import { COACH_TASK_ACTION_EVENT, type CoachTaskActionDetail } from "../coach-task-actions";
import type {
  DailyItemMove,
  DailyPatch,
  DailyUpdateOutcome,
} from "./disciplio-context-state";

type DisciplioContextValue = {
  state: AppState;
  getItemsForDate: (dateKey: string) => DailyItem[];
  ensurePlanLoaded: (dateKey: string, force?: boolean) => Promise<void>;
  refreshRemoteState: () => Promise<void>;
  retryRemoteState: () => Promise<void>;
  remoteRefreshRevision: number;
  remoteLoadError: boolean;
  sessionRestoreError: DisciplioApiError | null;
  retrySessionRestore: () => Promise<void>;
  setRemoteRefreshBlocked: (blocked: boolean) => void;
  dailyItemCreationRevision: number;
  toggleItem: (id: string, checked: boolean, dateKey?: string) => void;
  addItem: (
    item: DailyItem,
    dateKey?: string,
    notifyCreation?: boolean
  ) => Promise<boolean>;
  updateItem: (
    id: string,
    patch: DailyPatch,
    dateKey?: string,
    habitTimeScope?: HabitTimeScope,
    calendarHabitTimeScopeMode?: CalendarHabitTimeScopeMode
  ) => Promise<DailyUpdateOutcome>;
  deleteItem: (
    id: string,
    dateKey?: string,
    habitScope?: "ONE" | "ALL"
  ) => void;
  restoreItem: (item: DailyItem, dateKey?: string) => void;
  moveItem: (
    fromDateKey: string,
    toDateKey: string,
    itemId: string,
    patch?: DailyPatch
  ) => Promise<boolean>;
  moveDailyItems: (moves: DailyItemMove[]) => Promise<boolean>;
  duplicateItem: (
    itemId: string,
    sourceDateKey: string,
    targetDateKeys: string[],
    keepTime: boolean,
    keepWeeklyGoalConnected: boolean
  ) => Promise<boolean>;
  saveProfile: (profile: UserProfile) => void;
  saveSettings: (settings: DisciplioSettings) => void;
  rememberMetricUnit: (unit: string) => void;
  setFeedback: (text: string) => void;
  dismissSuggestion: (id: string) => void;
  addMonthlyGoal: (goal: MonthlyGoal) => void;
  updateMonthlyGoal: (
    id: string,
    patch: Partial<
      Pick<
        MonthlyGoal,
        | "title"
        | "target"
        | "unit"
        | "progress"
        | "yearGoal"
        | "yearGoalId"
        | "completed"
      >
    >
  ) => void;
  completeMonthlyGoal: (id: string, completed: boolean) => Promise<boolean>;
  deleteMonthlyGoal: (id: string) => void;
  replaceMonthlyGoals: (goals: MonthlyGoal[]) => void;
  addWeeklyGoal: (weekStartDate: string, goal: WeeklyGoal) => void;
  updateWeeklyGoal: (
    id: string,
    patch: Partial<
      Pick<WeeklyGoal, "title" | "target" | "unit" | "category" | "completed">
    >
  ) => void;
  deleteWeeklyGoal: (id: string) => void;
  replaceWeeklyGoals: (goals: WeeklyGoal[]) => void;
  streakRevision: number;
  refreshStreak: () => void;
  replaceRagSources: (sources: RagSource[]) => void;
  addRagSource: (source: RagSource) => void;
  updateRagSource: (id: string, patch: Partial<RagSource>) => void;
  deleteRagSource: (id: string) => void;
  suggestionPrefill: ActionPrefill | null;
  setSuggestionPrefill: (prefill: ActionPrefill | null) => void;
  goalCreationDrafts: GoalCreationDrafts;
  updateDailyGoalCreationDraft: (
    dateKey: string,
    draft: DailyGoalCreationDraft | null
  ) => void;
  updateWeeklyGoalCreationDraft: (
    weekStartDate: string,
    draft: WeeklyGoalCreationDraft | null
  ) => void;
  updateMonthlyGoalCreationDraft: (
    monthKey: string,
    draft: MonthlyGoalCreationDraft | null
  ) => void;
  updateYearGoalCreationDraft: (
    year: number,
    draft: YearGoalCreationDraft | null
  ) => void;
  updateGlobalGoalCreationDraft: (draft: GlobalGoalCreationDraft) => void;
  isGoalCreationDraftsHydrated: boolean;
  isRemoteHydrated: boolean;
  isSessionReady: boolean;
};

const DisciplioContext = createContext<DisciplioContextValue | null>(null);

export function DisciplioProvider({ children }: { children: ReactNode }) {
  const { isOptionalStorageEnabled } = useBrowserStorageConsent();
  const [state, dispatch] = useReducer(
    reducer,
    isOptionalStorageEnabled,
    (allowOptionalStorage) => loadState(allowOptionalStorage)
  );
  const [suggestionPrefill, setSuggestionPrefill] =
    useState<ActionPrefill | null>(null);
  const [goalCreationDrafts, setGoalCreationDrafts] =
    useState<GoalCreationDrafts>(() => emptyGoalCreationDrafts());
  const [isGoalCreationDraftsHydrated, setIsGoalCreationDraftsHydrated] =
    useState(false);
  const [isRemoteHydrated, setIsRemoteHydrated] = useState(false);
  const [isSessionReady, setIsSessionReady] = useState(false);
  const [sessionRestoreError, setSessionRestoreError] = useState<DisciplioApiError | null>(null);
  const [streakRevision, setStreakRevision] = useState(0);
  const [dailyItemCreationRevision, setDailyItemCreationRevision] = useState(0);
  const [sessionActive, setSessionActive] = useState(() => api.hasApiSession());
  const goalDraftOwnerRef = useRef<string>("");
  const initialProfileRef = useRef(state.profile);
  const initialSettingsRef = useRef(state.settings);
  const pendingDailyDatesRef = useRef<Set<string>>(new Set());
  const monthlyGoalMutationRevisionRef = useRef(0);
  const remoteRefreshBlockedRef = useRef(false);

  const restoreSession = useCallback(async () => {
    const restored = await api.restoreApiSession();
    const refreshError = api.getLastRefreshError();
    const transientFailure = refreshError && refreshError.status !== 400 && refreshError.status !== 401;
    setSessionRestoreError(!restored && transientFailure ? refreshError : null);
    setSessionActive(api.hasApiSession());
    setIsSessionReady(true);
  }, []);

  useEffect(() => {
    let active = true;
    void restoreSession().finally(() => {
      if (!active) return;
    });
    return () => {
      active = false;
    };
  }, [restoreSession]);

  const retrySessionRestore = useCallback(async () => {
    setIsSessionReady(false);
    await restoreSession();
  }, [restoreSession]);
  const ensurePlanLoaded = usePlanLoader(dispatch, pendingDailyDatesRef);
  const refreshStreak = useCallback(() => {
    setStreakRevision((revision) => revision + 1);
  }, []);

  usePersistedDisciplioState(state, isOptionalStorageEnabled);
  const {
    refreshRemoteState,
    retryRemoteState,
    refreshRevision: remoteRefreshRevision,
    remoteLoadError,
  } = useRemoteStateSynchronizer(
    dispatch,
    initialProfileRef.current,
    initialSettingsRef.current,
    setIsRemoteHydrated,
    pendingDailyDatesRef,
    monthlyGoalMutationRevisionRef,
    remoteRefreshBlockedRef,
    sessionActive,
    isSessionReady
  );
  const setRemoteRefreshBlocked = useCallback((blocked: boolean) => {
    remoteRefreshBlockedRef.current = blocked;
  }, []);
  const notifyDailyItemCreated = useCallback(() => {
    setDailyItemCreationRevision((revision) => revision + 1);
  }, []);
  const actions = useDisciplioActions(
    state,
    dispatch,
    pendingDailyDatesRef,
    monthlyGoalMutationRevisionRef,
    refreshStreak,
    refreshRemoteState,
    notifyDailyItemCreated
  );

  useEffect(() => {
    if (!isSessionReady || !sessionActive || typeof window === "undefined") return;
    const onCoachTaskAction = (event: Event) => {
      const detail = (event as CustomEvent<CoachTaskActionDetail>).detail;
      const dates = Array.isArray(detail?.affectedDates)
        ? [...new Set(detail.affectedDates.filter((date) => /^\d{4}-\d{2}-\d{2}$/.test(date)))]
        : [];
      if (dates.length === 0) {
        void refreshRemoteState();
        return;
      }
      void Promise.all(dates.map((date) => ensurePlanLoaded(date, true))).then(async () => {
        try {
          const history = await api.getCompletionHistory(730, state.settings);
          dispatch({ type: "replaceCompletionHistory", history });
        } catch {
          // The task mutation already succeeded; a later synchronizer refresh can repair history.
        }
        refreshStreak();
      });
    };
    window.addEventListener(COACH_TASK_ACTION_EVENT, onCoachTaskAction);
    return () => window.removeEventListener(COACH_TASK_ACTION_EVENT, onCoachTaskAction);
  }, [ensurePlanLoaded, isSessionReady, refreshRemoteState, refreshStreak, sessionActive, state.settings]);

  useEffect(() => {
    if (!isRemoteHydrated || !isOptionalStorageEnabled) {
      goalDraftOwnerRef.current = "";
      setGoalCreationDrafts(emptyGoalCreationDrafts());
      setIsGoalCreationDraftsHydrated(false);
      return;
    }
    const ownerEmail = goalDraftOwnerEmail(state.profile.email);
    if (!ownerEmail) {
      goalDraftOwnerRef.current = "";
      setGoalCreationDrafts(emptyGoalCreationDrafts());
      setIsGoalCreationDraftsHydrated(false);
      return;
    }
    if (goalDraftOwnerRef.current === ownerEmail) {
      setIsGoalCreationDraftsHydrated(true);
      return;
    }
    goalDraftOwnerRef.current = ownerEmail;
    setGoalCreationDrafts(loadGoalCreationDrafts(ownerEmail));
    setIsGoalCreationDraftsHydrated(true);
  }, [isOptionalStorageEnabled, isRemoteHydrated, state.profile.email]);

  useEffect(() => {
    if (!isOptionalStorageEnabled || !goalDraftOwnerRef.current) return;
    saveGoalCreationDrafts(goalDraftOwnerRef.current, goalCreationDrafts);
  }, [goalCreationDrafts, isOptionalStorageEnabled]);

  const updateDailyGoalCreationDraft = useCallback(
    (dateKey: string, draft: DailyGoalCreationDraft | null) => {
      if (!isGoalCreationDraftsHydrated) return;
      setGoalCreationDrafts((current) => {
        const dailyByDate = { ...current.dailyByDate };
        if (draft) dailyByDate[dateKey] = draft;
        else delete dailyByDate[dateKey];
        return { ...current, dailyByDate };
      });
    },
    [isGoalCreationDraftsHydrated]
  );

  const updateWeeklyGoalCreationDraft = useCallback(
    (weekStartDate: string, draft: WeeklyGoalCreationDraft | null) => {
      if (!isGoalCreationDraftsHydrated) return;
      setGoalCreationDrafts((current) => {
        const weeklyByStart = { ...current.weeklyByStart };
        if (draft) weeklyByStart[weekStartDate] = draft;
        else delete weeklyByStart[weekStartDate];
        return { ...current, weeklyByStart };
      });
    },
    [isGoalCreationDraftsHydrated]
  );

  const updateMonthlyGoalCreationDraft = useCallback(
    (monthKey: string, draft: MonthlyGoalCreationDraft | null) => {
      if (!isGoalCreationDraftsHydrated) return;
      setGoalCreationDrafts((current) => {
        const monthlyByMonth = { ...current.monthlyByMonth };
        if (draft) monthlyByMonth[monthKey] = draft;
        else delete monthlyByMonth[monthKey];
        return { ...current, monthlyByMonth };
      });
    },
    [isGoalCreationDraftsHydrated]
  );

  const updateYearGoalCreationDraft = useCallback(
    (year: number, draft: YearGoalCreationDraft | null) => {
      if (!isGoalCreationDraftsHydrated) return;
      const yearKey = String(year);
      setGoalCreationDrafts((current) => {
        const yearlyByYear = { ...current.yearlyByYear };
        if (draft) yearlyByYear[yearKey] = draft;
        else delete yearlyByYear[yearKey];
        return { ...current, yearlyByYear };
      });
    },
    [isGoalCreationDraftsHydrated]
  );

  const updateGlobalGoalCreationDraft = useCallback(
    (draft: GlobalGoalCreationDraft) => {
      if (!isGoalCreationDraftsHydrated) return;
      setGoalCreationDrafts((current) => ({ ...current, global: draft }));
    },
    [isGoalCreationDraftsHydrated]
  );

  const getItemsForDate = useCallback(
    (dateKey: string) => state.dailyItemsByDate[dateKey] ?? [],
    [state.dailyItemsByDate]
  );

  const value = useMemo(
    () => ({
      state,
      getItemsForDate,
      ensurePlanLoaded,
      refreshRemoteState,
      retryRemoteState,
      remoteRefreshRevision,
      remoteLoadError,
      sessionRestoreError,
      retrySessionRestore,
      streakRevision,
      refreshStreak,
      setRemoteRefreshBlocked,
      dailyItemCreationRevision,
      ...actions,
      suggestionPrefill,
      setSuggestionPrefill,
      goalCreationDrafts,
      updateDailyGoalCreationDraft,
      updateWeeklyGoalCreationDraft,
      updateMonthlyGoalCreationDraft,
      updateYearGoalCreationDraft,
      updateGlobalGoalCreationDraft,
      isGoalCreationDraftsHydrated,
      isRemoteHydrated,
      isSessionReady,
    }),
    [
      state,
      getItemsForDate,
      ensurePlanLoaded,
      refreshRemoteState,
      retryRemoteState,
      remoteRefreshRevision,
      remoteLoadError,
      sessionRestoreError,
      retrySessionRestore,
      streakRevision,
      refreshStreak,
      setRemoteRefreshBlocked,
      dailyItemCreationRevision,
      actions,
      suggestionPrefill,
      goalCreationDrafts,
      updateDailyGoalCreationDraft,
      updateWeeklyGoalCreationDraft,
      updateMonthlyGoalCreationDraft,
      updateYearGoalCreationDraft,
      updateGlobalGoalCreationDraft,
      isGoalCreationDraftsHydrated,
      isRemoteHydrated,
      isSessionReady,
    ]
  );

  return (
    <DisciplioContext.Provider value={value}>
      {children}
    </DisciplioContext.Provider>
  );
}

export function useDisciplio(): DisciplioContextValue {
  const ctx = useContext(DisciplioContext);
  if (!ctx)
    throw new Error("useDisciplio must be used within DisciplioProvider");
  return ctx;
}
